// A badge medallion preview (simple SVG; the app draws its own): a notched rim
// in the rarity colour, a glowing disc and a pictogram for the badge's family.

import { useId } from "react";
import type { BadgeRarity } from "../api/schemas";
import styles from "./medallion.module.css";

/** Pictograms on a 24×24 grid, by badge family (contracts/phase2.md §6). */
const ICONS: Record<string, string> = {
  flame: "M12 3c.8 3.6-2.6 5-2.6 8.4 0 1.2.6 2.2 1.4 2.7-.4-1.7.8-3.2 2.5-3.9-.4 2 .8 2.9 1.2 4 .6 1.2.2 2.4-.4 3.2A5.3 5.3 0 0 0 18 12.6C18 8 13.7 6.4 12 3Zm-4 8.5A5.8 5.8 0 0 0 6 16a6 6 0 0 0 12 .2 5.8 5.8 0 0 1-6 2.8c-3.1-.4-4.6-4-4-7.5Z",
  star: "M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.9L12 17l-5.2 2.7 1-5.9-4.3-4.1 5.9-.8Z",
  bolt: "M13.5 2.5 5.5 13.5h5l-1 8 8-11h-5Z",
  sun: "M12 7.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9ZM11 2h2v3h-2Zm0 17h2v3h-2ZM2 11h3v2H2Zm17 0h3v2h-3Z",
  moon: "M14.5 3.5a8.5 8.5 0 1 0 6 13.9A7 7 0 0 1 14.5 3.5Z",
  spark: "M12 2.5c.7 4.6 2.9 6.8 7.5 7.5-4.6.7-6.8 2.9-7.5 7.5-.7-4.6-2.9-6.8-7.5-7.5 4.6-.7 6.8-2.9 7.5-7.5Zm6 12c.3 1.9 1.1 2.7 3 3-1.9.3-2.7 1.1-3 3-.3-1.9-1.1-2.7-3-3 1.9-.3 2.7-1.1 3-3Z",
  check: "M4.5 12.5 9.5 17.5 19.5 6.5 17.6 4.8 9.4 13.8 6.3 10.7Z",
  layers: "M12 3 2.5 8 12 13l9.5-5Zm-7.3 8.3L2.5 12.5 12 17.5l9.5-5-2.2-1.2L12 15.2Zm0 4.5-2.2 1.2L12 22l9.5-5-2.2-1.2L12 19.7Z",
  people: "M8.5 11a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7Zm8-1a3 3 0 1 1 0-6 3 3 0 0 1 0 6ZM2 19.5c0-3.3 2.9-6 6.5-6s6.5 2.7 6.5 6v1H2Zm14.5-7.5c3 0 5.5 2.2 5.5 5v1.5h-5V18c0-2.2-.9-4.2-2.4-5.6.6-.3 1.2-.4 1.9-.4Z",
  diamond: "M12 2.5 21 12l-9 9.5L3 12Z",
  arrow: "M12 3.5 19 10.5h-4.5V20h-5v-9.5H5Z",
};

const FAMILY: Record<string, keyof typeof ICONS> = {
  week_one: "flame",
  fortnight: "flame",
  month_one: "flame",
  century: "flame",
  comeback: "flame",
  top_10: "star",
  top_1: "star",
  hard_top_10: "star",
  under_minute: "bolt",
  lightning: "bolt",
  early_bird: "sun",
  night_owl: "moon",
  first_light: "spark",
  glow_up: "spark",
  perfect_week: "check",
  all_rounder: "check",
  first_chapter: "layers",
  star_collector: "layers",
  explorer: "layers",
  deep_diver: "layers",
  five_friends: "people",
  recruiter: "people",
  first_share: "arrow",
  hard_boiled: "diamond",
};

export function Medallion({
  id,
  rarity,
  size = 64,
  label,
  dim = false,
}: {
  id: string;
  rarity: BadgeRarity | null;
  size?: number;
  label?: string;
  dim?: boolean;
}) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const icon = ICONS[FAMILY[id] ?? "star"]!;
  const notches = Array.from({ length: 24 }, (_, i) => i * 15);
  return (
    <svg
      className={styles.medallion}
      data-rarity={rarity ?? "common"}
      data-dim={dim || undefined}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label={label ?? `${id} medallion`}
    >
      <defs>
        <radialGradient id={`disc-${uid}`} cx="38%" cy="30%" r="75%">
          <stop offset="0" className={styles.discLight} />
          <stop offset="1" className={styles.discDark} />
        </radialGradient>
      </defs>
      <circle cx="32" cy="32" r="30" className={styles.glow} />
      {notches.map((a) => (
        <rect key={a} x="31" y="1.5" width="2" height="5" rx="1" className={styles.notch} transform={`rotate(${a} 32 32)`} />
      ))}
      <circle cx="32" cy="32" r="24.5" className={styles.rim} />
      <circle cx="32" cy="32" r="21" fill={`url(#disc-${uid})`} />
      <g transform="translate(20 20)">
        <path d={icon} className={styles.icon} fillRule="evenodd" />
      </g>
    </svg>
  );
}

export const RARITY_TONE: Record<BadgeRarity, string> = { common: "neutral", uncommon: "success", rare: "info", epic: "violet" };
